import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

export interface EditorDocument {
  id: string
  title: string
  content: string
  createdAt: string
  updatedAt: string
}

export interface EditorIssue {
  id: string
  type: 'spelling' | 'grammar' | 'fidel'
  position: { from: number; to: number }
  text: string
  message: string
  suggestions: string[]
}

interface EditorState {
  // Current document
  currentDocument: EditorDocument | null
  setCurrentDocument: (doc: EditorDocument | null) => void
  updateDocumentContent: (content: string) => void
  updateDocumentTitle: (title: string) => void

  // Documents list
  documents: EditorDocument[]
  addDocument: (doc: EditorDocument) => void
  removeDocument: (id: string) => void

  // Issues/Errors
  issues: EditorIssue[]
  setIssues: (issues: EditorIssue[]) => void
  addIssue: (issue: EditorIssue) => void
  removeIssue: (id: string) => void
  clearIssues: () => void

  // UI State
  showSidebar: boolean
  toggleSidebar: () => void
  sidebarTab: 'issues' | 'statistics'
  setSidebarTab: (tab: 'issues' | 'statistics') => void

  // Editor settings
  fontSize: number
  setFontSize: (size: number) => void
  fontFamily: string
  setFontFamily: (family: string) => void
  showLineNumbers: boolean
  toggleLineNumbers: () => void

  // Autocomplete
  showAutocomplete: boolean
  setShowAutocomplete: (show: boolean) => void
  autocompleteSuggestions: string[]
  setAutocompleteSuggestions: (suggestions: string[]) => void
  ghostText: string
  setGhostText: (text: string) => void

  // Actions
  createNewDocument: () => void
  saveDocument: () => void
  reset: () => void
}

const generateId = () => Math.random().toString(36).substring(2, 15)

const createNewDoc = (): EditorDocument => ({
  id: generateId(),
  title: 'Untitled Document',
  content: '',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
})

export const useEditorStore = create<EditorState>()(
  devtools(
    persist(
      (set, get) => ({
        // Current document
        currentDocument: null,
        setCurrentDocument: (doc) => set({ currentDocument: doc }),
        updateDocumentContent: (content) =>
          set((state) => ({
            currentDocument: state.currentDocument
              ? { ...state.currentDocument, content, updatedAt: new Date().toISOString() }
              : null
          })),
        updateDocumentTitle: (title) =>
          set((state) => ({
            currentDocument: state.currentDocument
              ? { ...state.currentDocument, title, updatedAt: new Date().toISOString() }
              : null
          })),

        // Documents list
        documents: [],
        addDocument: (doc) =>
          set((state) => ({
            documents: [...state.documents.filter((d) => d.id !== doc.id), doc]
          })),
        removeDocument: (id) =>
          set((state) => ({
            documents: state.documents.filter((d) => d.id !== id),
            currentDocument:
              state.currentDocument?.id === id ? null : state.currentDocument
          })),

        // Issues
        issues: [],
        setIssues: (issues) => set({ issues }),
        addIssue: (issue) =>
          set((state) => ({ issues: [...state.issues, issue] })),
        removeIssue: (id) =>
          set((state) => ({ issues: state.issues.filter((i) => i.id !== id) })),
        clearIssues: () => set({ issues: [] }),

        // UI State
        showSidebar: true,
        toggleSidebar: () => set((state) => ({ showSidebar: !state.showSidebar })),
        sidebarTab: 'issues',
        setSidebarTab: (tab) => set({ sidebarTab: tab }),

        // Editor settings
        fontSize: 16,
        setFontSize: (size) => set({ fontSize: size }),
        fontFamily: 'Noto Sans Ethiopic',
        setFontFamily: (family) => set({ fontFamily: family }),
        showLineNumbers: false,
        toggleLineNumbers: () =>
          set((state) => ({ showLineNumbers: !state.showLineNumbers })),

        // Autocomplete
        showAutocomplete: false,
        setShowAutocomplete: (show) => set({ showAutocomplete: show }),
        autocompleteSuggestions: [],
        setAutocompleteSuggestions: (suggestions) =>
          set({ autocompleteSuggestions: suggestions }),
        ghostText: '',
        setGhostText: (text) => set({ ghostText: text }),

        // Actions
        createNewDocument: () => {
          const newDoc = createNewDoc()
          set((state) => ({
            currentDocument: newDoc,
            documents: [...state.documents, newDoc],
            issues: []
          }))
        },
        saveDocument: () => {
          const { currentDocument, documents } = get()
          if (currentDocument) {
            const updatedDoc = {
              ...currentDocument,
              updatedAt: new Date().toISOString()
            }
            set({
              currentDocument: updatedDoc,
              documents: documents.map((d) =>
                d.id === updatedDoc.id ? updatedDoc : d
              )
            })
          }
        },
        reset: () =>
          set({
            currentDocument: null,
            issues: [],
            showAutocomplete: false,
            autocompleteSuggestions: [],
            ghostText: ''
          })
      }),
      {
        name: 'editor-store',
        partialize: (state: EditorState) => ({
          documents: state.documents,
          fontSize: state.fontSize,
          fontFamily: state.fontFamily,
          showSidebar: state.showSidebar
        })
      }
    ),
    { name: 'editor-store' }
  )
)
